import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  EmptyState,
  FullscreenSpinner,
} from '@shared/ui';
import { buyRequestApi } from '@entities/buy-request';
import { MediaDownloadLink } from '@entities/media';
import { formatDateTime } from '@shared/lib/format';
import { useHideBuyRequest, useUnhideBuyRequest } from '@features/buy-request-moderation';

export function BuyRequestDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const [hideOpen, setHideOpen] = useState(false);
  const [unhideOpen, setUnhideOpen] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['buy-request', id],
    queryFn: () => buyRequestApi.get(id),
    enabled: !!id,
  });

  const hide = useHideBuyRequest(id);
  const unhide = useUnhideBuyRequest(id);

  if (isLoading) return <FullscreenSpinner />;
  if (isError || !data) return <EmptyState title="Не найдено" />;

  return (
    <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
      <div className="space-y-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{data.name}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <Badge variant="outline">{data.status}</Badge>
            <Badge variant="outline">{data.dispatchMode}</Badge>
            <span>создана {formatDateTime(data.createdAt)}</span>
          </div>
        </div>

        {data.hiddenByAdmin ? (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
            <div className="font-medium text-destructive">Скрыта администратором</div>
            <p className="mt-1 text-foreground/80">{data.hiddenReason ?? '—'}</p>
          </div>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle>Описание</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap text-sm">{data.description || '—'}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Параметры</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <dt className="text-muted-foreground">Объём</dt>
              <dd>{data.volume ?? '—'}</dd>
              <dt className="text-muted-foreground">Бюджет</dt>
              <dd>{data.budget != null ? `${data.budget} ${data.currency ?? ''}` : '—'}</dd>
              <dt className="text-muted-foreground">Страна назначения</dt>
              <dd>{data.destinationCountry}</dd>
              <dt className="text-muted-foreground">Котировок</dt>
              <dd>{data.quoteCount}</dd>
            </dl>
          </CardContent>
        </Card>

        {data.attachments.length > 0 ? (
          <Card>
            <CardHeader>
              <CardTitle>Вложения</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-1 text-sm">
                {data.attachments.map((a) => (
                  <li key={a.id}>
                    <MediaDownloadLink link={a}>
                      Вложение · {a.contentType?.split('/')[1] ?? 'file'}
                    </MediaDownloadLink>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ) : null}
      </div>

      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Модерация</CardTitle>
          </CardHeader>
          <CardContent>
            {data.hiddenByAdmin ? (
              <Button variant="success" className="w-full" onClick={() => setUnhideOpen(true)}>
                Unhide
              </Button>
            ) : (
              <Button variant="destructive" className="w-full" onClick={() => setHideOpen(true)}>
                Hide
              </Button>
            )}
          </CardContent>
        </Card>
      </div>

      <ConfirmDialog
        open={hideOpen}
        onOpenChange={setHideOpen}
        title="Скрыть заявку?"
        description="Заявка не будет показываться производителям."
        confirmLabel="Hide"
        variant="destructive"
        withReason
        reasonLabel="Причина"
        isPending={hide.isPending}
        onConfirm={(reason) => hide.mutate(reason, { onSuccess: () => setHideOpen(false) })}
      />
      <ConfirmDialog
        open={unhideOpen}
        onOpenChange={setUnhideOpen}
        title="Восстановить заявку?"
        description="Заявка снова появится в feed производителей."
        confirmLabel="Unhide"
        variant="success"
        isPending={unhide.isPending}
        onConfirm={() => unhide.mutate(undefined, { onSuccess: () => setUnhideOpen(false) })}
      />
    </div>
  );
}
